import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import DatePicker from "react-date-picker";
import AsyncSelect from "react-select/async";

import ai from "../../helpers/axios";

const PFPSubscriptions = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [expanded, setExpanded] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [departureAirport, setDepartureAirport] = useState(null);
  const [arrivalAirport, setArrivalAirport] = useState(null);
  const [dateRangeStart, setDateRangeStart] = useState(null);
  const [dateRangeEnd, setDateRangeEnd] = useState(null);

  const getToken = async () => {
    return getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
  };

  const loadAirportOptions = async (inputValue) => {
    const ip =
      Math.floor(Math.random() * 255) + 1 + "." +
      Math.floor(Math.random() * 255) + "." +
      Math.floor(Math.random() * 255) + "." +
      Math.floor(Math.random() * 255);
    const token = await getToken();
    const { data } = await ai
      .auth(token)
      .get(`/api/airports/search?q=${inputValue}&ip_address=${ip}`);

    if (data?.length) {
      return data.map((i) => ({
        value: i.code,
        label: i.airportName,
        code: i.code,
        city: i.city,
      }));
    }
    return [];
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    const token = await getToken();
    const response = await ai
      .auth(token)
      .get("/api/pfpSubscriptions/")
      .catch((err) => console.error(err));

    if (response?.data?.data) {
      setSubscriptions(response.data.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (expanded) fetchSubscriptions();
  }, [expanded]);

  const handleSubscribe = async () => {
    if (!departureAirport || !arrivalAirport) return;
    setSubmitting(true);

    const token = await getToken();
    await ai
      .auth(token)
      .post("/api/pfpSubscriptions/", {
        departureAirportCode: departureAirport.code,
        arrivalAirportCode: arrivalAirport.code,
        dateRangeStart: dateRangeStart || null,
        dateRangeEnd: dateRangeEnd || null,
      })
      .catch((err) => console.error(err));

    setDepartureAirport(null);
    setArrivalAirport(null);
    setDateRangeStart(null);
    setDateRangeEnd(null);
    setSubmitting(false);
    fetchSubscriptions();
  };

  const handleDelete = async (id) => {
    const token = await getToken();
    await ai
      .auth(token)
      .delete(`/api/pfpSubscriptions/${id}`)
      .catch((err) => console.error(err));
    fetchSubscriptions();
  };

  return (
    <div className="pfp-subscriptions">
      <button
        className={`pfp-subscriptions__toggle${expanded ? " pfp-subscriptions__toggle--active" : ""}`}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? "Hide" : "Route Subscriptions"}
      </button>

      <div className={`pfp-subscriptions__panel-wrapper${expanded ? " pfp-subscriptions__panel-wrapper--open" : ""}`}>
        <div className="pfp-subscriptions__panel-inner">
          <div className="pfp-subscriptions__panel">
            <div className="pfp-subscriptions__form">
              <div className="pfp-subscriptions__field">
                <label className="pfp-subscriptions__label">
                  Departure Airport
                </label>
                <AsyncSelect
                  loadOptions={loadAirportOptions}
                  placeholder="Search airport..."
                  onChange={setDepartureAirport}
                  value={departureAirport}
                  isClearable
                  cacheOptions
                  styles={{
                    control: (base) => ({ ...base, fontSize: 13 }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="pfp-subscriptions__field">
                <label className="pfp-subscriptions__label">
                  Arrival Airport
                </label>
                <AsyncSelect
                  loadOptions={loadAirportOptions}
                  placeholder="Search airport..."
                  onChange={setArrivalAirport}
                  value={arrivalAirport}
                  isClearable
                  cacheOptions
                  styles={{
                    control: (base) => ({ ...base, fontSize: 13 }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="pfp-subscriptions__field--date">
                <label className="pfp-subscriptions__label">
                  From (optional)
                </label>
                <DatePicker
                  onChange={setDateRangeStart}
                  value={dateRangeStart}
                  clearIcon={null}
                  calendarIcon={null}
                  format="MM/dd/yyyy"
                  className="pfp-date-picker"
                />
              </div>
              <div className="pfp-subscriptions__field--date">
                <label className="pfp-subscriptions__label">
                  To (optional)
                </label>
                <DatePicker
                  onChange={setDateRangeEnd}
                  value={dateRangeEnd}
                  clearIcon={null}
                  calendarIcon={null}
                  format="MM/dd/yyyy"
                  className="pfp-date-picker"
                />
              </div>
              <button
                onClick={handleSubscribe}
                disabled={!departureAirport || !arrivalAirport || submitting}
                className="pfp-subscriptions__submit"
              >
                {submitting ? "..." : "Subscribe"}
              </button>
            </div>

            {loading && <div className="pfp-subscriptions__loading">Loading subscriptions...</div>}

            {!loading && subscriptions.length === 0 && (
              <div className="pfp-subscriptions__empty">No active subscriptions.</div>
            )}

            {!loading && subscriptions.length > 0 && (
              <div className="pfp-subscriptions__table-wrapper">
                <table className="pfp-subscriptions__table">
                  <thead>
                    <tr>
                      <th>Route</th>
                      <th>Date Range</th>
                      <th>Created</th>
                      <th style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub) => (
                      <tr key={sub._id}>
                        <td>
                          {sub.departureAirport?.code} → {sub.arrivalAirport?.code}
                        </td>
                        <td>
                          {sub.dateRangeStart || sub.dateRangeEnd
                            ? `${sub.dateRangeStart ? new Date(sub.dateRangeStart).toLocaleDateString() : "Any"} – ${sub.dateRangeEnd ? new Date(sub.dateRangeEnd).toLocaleDateString() : "Any"}`
                            : "Any dates"}
                        </td>
                        <td>
                          {new Date(sub.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <button
                            onClick={() => handleDelete(sub._id)}
                            className="pfp-subscriptions__remove"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PFPSubscriptions;
